import { FC } from 'react'
import { useTranslation } from 'react-i18next'
import PerFlexRow from '../FlexRow'
import PerText from '../Text'
import { Currency } from '../../../types'
import { useCurrency } from '../../../context/currency-context'

interface Props {
  annualTargetBonus?: number
  openTargetBonus?: number
  targetBonusToPay?: number
  formatCurrency: (amount: number) => string
  calculateAndFormatNewTargetAmount: (amount: number, conversionRate?: number) => string
  totalTargets: number
  openTargets: number
  openTargetBonusIsNegative?: boolean
  unknownCurrenciesForConversion?: Currency[]
}

export const PastTargetsFooterTemplate: FC<Props> = ({
  annualTargetBonus,
  openTargetBonus,
  targetBonusToPay,
  formatCurrency,
  calculateAndFormatNewTargetAmount,
  totalTargets,
  openTargets,
  openTargetBonusIsNegative,
  unknownCurrenciesForConversion = [],
}) => {
  const [t] = useTranslation()

  const { currency: currentContractCurrency } = useCurrency()
  return (
    <>
      <PerFlexRow>
        <div>
          <PerText appearance="footer-label-small">{t('pastTargetsFooter.openTargets')}</PerText>
          <PerText appearance="footer-label-small">{t('pastTargetsFooter.totalTargets')}</PerText>
        </div>
        <div>
          <PerText appearance="footer-input-small">{openTargets}</PerText>
          <PerText appearance="footer-input-small">{totalTargets}</PerText>
        </div>
      </PerFlexRow>
      {annualTargetBonus !== undefined && annualTargetBonus > 0 && (
        <PerFlexRow align="middle">
          <PerFlexRow mr="xxxl">
            <div data-e2e-appraisal-past-targets-footer-bonus>
              <PerText appearance="footer-label-small">{t('pastTargetsFooter.contractualBonus')}</PerText>
              <PerText appearance="footer-label-small">{t('pastTargetsFooter.openTargetBonus')}</PerText>
            </div>
            <div data-e2-appraisal-past-targets-contractual-bonus-amount>
              <PerText appearance="footer-input-small">{formatCurrency(annualTargetBonus)}</PerText>
              <PerText
                appearance="footer-input-small"
                color={openTargetBonusIsNegative || unknownCurrenciesForConversion.length > 0 ? 'error' : 'success'}
                tooltip={
                  unknownCurrenciesForConversion.length > 0
                    ? t('currencyConversion.tooltips.unknownConversionRate', {
                        currentContractCurrency: currentContractCurrency,
                        unknownCurrencies: unknownCurrenciesForConversion.join(', '),
                      })
                    : undefined
                }
              >
                {openTargetBonus !== undefined && openTargetBonus !== null
                  ? unknownCurrenciesForConversion.length > 0
                    ? calculateAndFormatNewTargetAmount(openTargetBonus)
                    : formatCurrency(openTargetBonus)
                  : undefined}
              </PerText>
            </div>
          </PerFlexRow>
          <PerFlexRow>
            <PerText appearance="footer-label">{t('pastTargetsFooter.toBePaid')}</PerText>
            <PerText
              appearance="footer-input"
              color={unknownCurrenciesForConversion.length > 0 ? 'error' : undefined}
              tooltip={
                unknownCurrenciesForConversion.length > 0
                  ? t('currencyConversion.tooltips.unknownConversionRate', {
                      currentContractCurrency: currentContractCurrency,
                      unknownCurrencies: unknownCurrenciesForConversion.join(', '),
                    })
                  : undefined
              }
            >
              {targetBonusToPay
                ? unknownCurrenciesForConversion.length > 0
                  ? calculateAndFormatNewTargetAmount(targetBonusToPay)
                  : formatCurrency(targetBonusToPay)
                : undefined}
            </PerText>
          </PerFlexRow>
        </PerFlexRow>
      )}
    </>
  )
}
